<template>
  <div>
    <div class="d-flex align-center justify-space-between py-4 px-4">
      <span class="text-h6">Shopping Cart</span>
      <v-btn icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-divider />

    <div v-if="!isCartEmpty">
      <v-list>
        <v-list-item
          v-for="item in data.lineItems"
          twoLine
          :key="item.id"
        >
          <v-avatar tile class="mr-4">
            <product-image :product="item" :size="50" />
          </v-avatar>
          <v-list-item-content class="mr-4">
            <v-list-item-title class="text-wrap">{{ item.title }}</v-list-item-title>
            <template v-if="item.variant.title !== 'Default Title'">
              <v-list-item-subtitle v-for="(option, index) in item.variant.selectedOptions" :key="index">{{ option.name }}: {{ option.value }}</v-list-item-subtitle>
            </template>
          </v-list-item-content>

          <div class="d-flex align-end flex-column">
            <div class="mb-1">${{ item.variant.price }}</div>
            <v-text-field
              id="quantity"
              :value="item.quantity"
              outlined
              hideDetails
              singleLine
              type="number"
              class="centered-input"
              dense
              :rules="quantityRules"
              style="max-width: 40px"
              @input="quantity => updateItemQuantity(item, quantity)"
            />
          </div>
          <v-list-item-action>
            <v-btn icon @click="removeItem(item)">
              <v-icon color="red">mdi-close-circle</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <v-divider class="mb-4" />

      <div class="text-right pt-0 d-flex align-center justify-end col col-12">
        <span class="text-body-2 grey--text text--darken-1">{{ numberOfItemsStr }}</span>
        <div class="v-responsive grey--text subtitle text--darken-2 font-weight-medium shrink d-inline-flex justify-end" tag="span" style="min-width: 100px;">${{ data.subtotalPrice }}</div>
      </div>

      <div class="text-right pt-0 pb-0 d-flex align-center justify-end col col-12">
        <span class="text-body-2 primary--text text--darken-1">Preventia Bucks:</span>
        <div class="v-responsive primary--text subtitle  font-weight-medium shrink d-inline-flex justify-end" tag="span" style="min-width: 100px;">-${{ preventiaBucks }}</div>
      </div>
      <div class="text-right pt-0 d-flex align-center justify-end col col-12">
        <span class="text-body-2 grey--text text--darken-1">(${{ remainingPreventiaBucks }} remaining)</span>
        <div class="v-responsive primary--text subtitle  font-weight-medium shrink d-inline-flex justify-end" tag="span" style="min-width: 100px;"></div>
      </div>

      <v-divider class="mb-4" />
      <div class="text-right pt-0 d-flex align-center justify-end col col-12">
        <span class="text-body-2 grey--text text--darken-1">Total:</span>
        <div class="v-responsive grey--text headline text--darken-4 font-weight-medium shrink d-inline-flex justify-end" tag="span" style="min-width: 100px;">${{ total }}</div>
      </div>

      <div class="ma-8">
        <!-- todo: hijack chekcout button to add discount coupon -->
        <v-btn color="primary" :loading="checkoutLoading" @click="checkout">Checkout</v-btn>
      </div>
    </div>

    <v-list v-if="isCartEmpty">
      <div class="ma-8">
        <v-list-item class="mb-8">
          <v-list-item-title class="text-center body-2">Shopping Cart Empty</v-list-item-title>
        </v-list-item>

        <v-btn block color="primary" @click="close">Continue Shopping</v-btn>
      </div>
    </v-list>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ProductImage from '@/components/utilities/productImage';

export default {
  name: 'TheCart',
  components: {
    ProductImage
  },
  data: function () {
    return {
      headers: [
        {
          text: 'Product',
          value: 'title'
        },
        {
          text: 'Price',
          value: 'variant.price'
        }
      ],
      quantityRules: [
        quantity => !!quantity || 'This field is required',
        quantity => (quantity && quantity >= 1) || 'Quantity should be above 1'
      ],
      checkoutLoading: false
    };
  },
  methods: {
    close: function () {
      this.$emit('close');
    },
    removeItem: function (item) {
      const lineItemIdsToRemove = [
        item.id
      ];
      this.$store.dispatch('cart/removeItem', lineItemIdsToRemove);
    },
    updateItemQuantity: function (item, newQuantity) {
      // eslint-disable-next-line eqeqeq
      if (item.quantity != newQuantity) {
        const lineItemsToUpdate = [
          { id: item.id, quantity: parseInt(newQuantity) }
        ];
        this.$store.dispatch('cart/updateItemQuantities', lineItemsToUpdate);
      }
    },
    checkout: async function () {
      this.checkoutLoading = true;
      await this.$store.dispatch('cart/addPreventiaBucks');
      this.checkoutLoading = false;
      window.open(this.data.webUrl, '_blank');
    }
  },
  computed: {
    // todo: require 10 items to checkoout
    // add product page??
    // api for orders webhook to process discount and connect customer to order
    numberOfItems: function () {
      let ret = 0;
      for (const item of this.data.lineItems) {
        ret += item.quantity;
      }
      return ret;
    },
    numberOfItemsStr: function () {
      const numItems = this.numberOfItems;
      if (numItems > 1) {
        return `Subtotal: (${numItems} items):`;
      }
      return `Subtotal: (${numItems} item):`;
    },
    total: function () {
      return (this.data.subtotalPrice - this.preventiaBucks).toFixed(2);
    },
    isCartEmpty: function () {
      return !this.data || this.data.lineItems.length === 0;
    },
    preventiaBucks: function () {
      const preventiaBucks = this.currentUser.preventiaBucks;
      const subtotal = this.data.subtotalPrice;

      if (preventiaBucks > subtotal) {
        return subtotal;
      }
      return preventiaBucks;
    },
    remainingPreventiaBucks: function () {
      const preventiaBucks = this.currentUser.preventiaBucks;
      return (preventiaBucks - this.preventiaBucks).toFixed(2);
    },
    ...mapState('cart', ['data']),
    ...mapState(['currentUser'])
  }
};
</script>
